import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Aprendiz } from "@/interfaces/user.interface";
import { useUpdateAprendizWithUsuario } from "@/hooks/aprendices/useUpdateAprendizWithUsuario";

const schema = z.object({
  nombre: z.string().min(1, "Nombre requerido"),
  apellidoPaterno: z.string().min(1, "Apellido paterno requerido"),
  apellidoMaterno: z.string().optional(),
  correo: z.string().email("Correo inválido"),
});

type FormData = z.infer<typeof schema>;

export function EditAprendizDatosPersonalesForm({
  aprendiz,
  queryKey,
  close,
}: {
  aprendiz: Aprendiz;
  queryKey?: ReadonlyArray<unknown>;
  close: () => void;
}) {
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: aprendiz.usuario.nombre,
      apellidoPaterno: aprendiz.usuario.apellidoPaterno,
      apellidoMaterno: aprendiz.usuario.apellidoMaterno ?? "",
      correo: aprendiz.usuario.correo,
    },
  });

  const { mutate, isPending } = useUpdateAprendizWithUsuario(queryKey);

  const onSubmit = (values: FormData) => {
    const id =
      typeof aprendiz.id === "number" ? aprendiz.id : Number(aprendiz.id);
    mutate(
      {
        id,
        data: {
          usuario: {
            nombre: values.nombre,
            apellidoPaterno: values.apellidoPaterno,
            apellidoMaterno: values.apellidoMaterno || undefined,
            correo: values.correo,
          },
        },
      },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <FormField
          control={form.control}
          name="nombre"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="apellidoPaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Apellido paterno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido paterno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="apellidoMaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Apellido materno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido materno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="correo"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Correo</FormLabel>
              <FormControl>
                <Input placeholder="correo@ejemplo.com" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={isPending}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isPending ? "Guardando..." : "Guardar cambios"}
        </Button>
      </form>
    </Form>
  );
}
